package br.com.texeratx.boardgametimeapp.ui.screens.players

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Badge
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.core.i18n.Strings
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppSectionCard
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import br.com.texeratx.boardgametimeapp.ui.screens.players.create.CreatePlayerScreen
import br.com.texeratx.boardgametimeapp.ui.screens.players.model.PlayerListItemUI
import cafe.adriel.voyager.navigator.currentOrThrow

@Composable
fun PlayersUI(
    state: PlayersUiState,
    onSortChange: (PlayerSortOption) -> Unit,
    onDismissError: () -> Unit,
    onSearch: (String) -> Unit,
    onApplyFilters: (PlayersFilter) -> Unit,
    onBack: () -> Unit,
    onPlayerClick: (Long) -> Unit
) {
    val S = LocalStrings.current
    val navigator = cafe.adriel.voyager.navigator.LocalNavigator.currentOrThrow
    var showFilters by remember { mutableStateOf(false) }

    AppScreenScaffold(
        topBar = {
            PlayersTopBar(
                selected = state.sort,
                count = state.players.size,
                onSortChange = onSortChange,
                onBack = onBack,
                currentQuery = state.searchQuery,
                onSearch = onSearch,
                onOpenFilters = { showFilters = true }
            )
        },
        floatingActionButton = {
            androidx.compose.material3.FloatingActionButton(onClick = { navigator.push(CreatePlayerScreen()) }) {
                Icon(Icons.Default.Add, contentDescription = S.addPlayer)
            }
        }
    ) { padding ->

        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            Column(modifier = Modifier.fillMaxSize()) {
                if (state.filters.hasActiveFilters()) {
                    ActivePlayerFiltersChips(
                        filters = state.filters,
                        onRemoveWithNickname = {
                            onApplyFilters(state.filters.copy(withNickname = false))
                        },
                        onRemoveWithoutNickname = {
                            onApplyFilters(state.filters.copy(withoutNickname = false))
                        },
                        onRemoveWithPhoto = {
                            onApplyFilters(state.filters.copy(withPhoto = false))
                        }
                    )
                }

                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(top = 16.dp, bottom = 88.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(state.players) { player ->
                        PlayerItem(
                            player = player,
                            onClick = { onPlayerClick(player.id) }
                        )
                    }
                }
            }

            if (state.isLoading) {
                Box(
                    Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }

            state.error?.let {
                AlertDialog(
                    onDismissRequest = onDismissError,
                    confirmButton = {
                        TextButton(onClick = onDismissError) {
                            Text(S.ok)
                        }
                    },
                    title = { Text(S.errorTitle) },
                    text = { Text(it) }
                )
            }
        }

        if (showFilters) {
            PlayersFilterBottomSheet(
                current = state.filters,
                onApply = {
                    onApplyFilters(it)
                    showFilters = false
                },
                onDismiss = { showFilters = false }
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlayersTopBar(
    selected: PlayerSortOption,
    count: Int,
    onSortChange: (PlayerSortOption) -> Unit,
    onBack: () -> Unit,
    currentQuery: String,
    onSearch: (String) -> Unit,
    onOpenFilters: () -> Unit
) {
    val S = LocalStrings.current
    var expanded by remember { mutableStateOf(false) }
    var isSearching by remember { mutableStateOf(currentQuery.isNotBlank()) }

    TopAppBar(
        title = {
            if (isSearching) {
                TextField(
                    value = currentQuery,
                    onValueChange = onSearch,
                    placeholder = { Text(S.searchPlayer) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = TextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent,
                        focusedIndicatorColor = Color.Transparent,
                        unfocusedIndicatorColor = Color.Transparent
                    )
                )
            } else {
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = S.navPlayers,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )

                    Spacer(Modifier.width(8.dp))

                    Badge(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ) {
                        Text(count.toString())
                    }
                }
            }
        },
        navigationIcon = {
            Icon(
                imageVector = Icons.Default.ArrowBack,
                contentDescription = S.back,
                modifier = Modifier
                    .padding(start = 12.dp)
                    .clickable { onBack() }
            )
        },
        colors = AppTopBarColors(),
        actions = {
            Icon(
                imageVector = if (isSearching) Icons.Default.Close else Icons.Default.Search,
                contentDescription = S.searchPlayer,
                modifier = Modifier
                    .padding(horizontal = 8.dp)
                    .clickable {
                        isSearching = !isSearching
                        if (!isSearching) onSearch("")
                    }
            )

            Icon(
                imageVector = Icons.Default.FilterList,
                contentDescription = S.filtersTitle,
                modifier = Modifier
                    .padding(horizontal = 8.dp)
                    .clickable { onOpenFilters() }
            )

            Row(
                modifier = Modifier
                    .padding(end = 12.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .clickable { expanded = true }
                    .background(
                        MaterialTheme.colorScheme.primary.copy(alpha = 0.15f)
                    )
                    .padding(horizontal = 10.dp, vertical = 6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    selected.label(S),
                    fontWeight = FontWeight.SemiBold
                )

                Spacer(Modifier.width(4.dp))

                Icon(
                    imageVector = Icons.Default.ArrowDropDown,
                    contentDescription = S.sortPlayersAZ
                )
            }

            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false }
            ) {
                DropdownMenuItem(
                    text = { Text(S.sortPlayersAZ) },
                    onClick = {
                        expanded = false
                        onSortChange(PlayerSortOption.NAME_ASC)
                    }
                )

                DropdownMenuItem(
                    text = { Text(S.sortPlayersZA) },
                    onClick = {
                        expanded = false
                        onSortChange(PlayerSortOption.NAME_DESC)
                    }
                )
            }
        }
    )
}

@Composable
fun PlayerItem(
    player: PlayerListItemUI,
    onClick: () -> Unit
) {
    AppSectionCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp),
        shape = RoundedCornerShape(16.dp),
        contentPadding = PaddingValues(12.dp),
        onClick = onClick,
    ) {
        Row(
            modifier = Modifier,
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (!player.imageUrl.isNullOrBlank()) {
                PlayerAvatar(
                    imageUrl = player.imageUrl,
                    contentDescription = player.name,
                    modifier = Modifier
                        .size(56.dp)
                        .clip(CircleShape)
                )
            } else {
                Box(
                    modifier = Modifier
                        .size(56.dp)
                        .clip(CircleShape)
                        .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.2f)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = player.name.first().uppercase(),
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            Spacer(Modifier.width(12.dp))

            Column {
                Text(
                    text = player.name,
                    fontWeight = FontWeight.Bold
                )

                if (!player.nickname.isNullOrBlank()) {
                    Text(
                        text = player.nickname,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlayersFilterBottomSheet(
    current: PlayersFilter,
    onApply: (PlayersFilter) -> Unit,
    onDismiss: () -> Unit
) {
    val S = LocalStrings.current
    var withNickname by remember { mutableStateOf(current.withNickname) }
    var withoutNickname by remember { mutableStateOf(current.withoutNickname) }
    var withPhoto by remember { mutableStateOf(current.withPhoto) }

    ModalBottomSheet(onDismissRequest = onDismiss) {
        Column(Modifier.padding(16.dp)) {
            Text(S.filtersTitle, style = MaterialTheme.typography.titleLarge)
            Spacer(Modifier.height(16.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    checked = withNickname,
                    onCheckedChange = { withNickname = it }
                )
                Text(S.filterWithNickname)
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    checked = withoutNickname,
                    onCheckedChange = { withoutNickname = it }
                )
                Text(S.filterWithoutNickname)
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    checked = withPhoto,
                    onCheckedChange = { withPhoto = it }
                )
                Text(S.filterWithPhoto)
            }

            Spacer(Modifier.height(24.dp))

            Button(
                modifier = Modifier.fillMaxWidth(),
                onClick = {
                    onApply(
                        PlayersFilter(
                            withNickname = withNickname,
                            withoutNickname = withoutNickname,
                            withPhoto = withPhoto
                        )
                    )
                }
            ) {
                Text(S.applyFilters)
            }

            Spacer(Modifier.height(12.dp))

            TextButton(
                modifier = Modifier.fillMaxWidth(),
                onClick = { onApply(PlayersFilter()) }
            ) {
                Text(S.clearFilters)
            }
        }
    }
}

@Composable
fun ActivePlayerFiltersChips(
    filters: PlayersFilter,
    onRemoveWithNickname: () -> Unit,
    onRemoveWithoutNickname: () -> Unit,
    onRemoveWithPhoto: () -> Unit
) {
    Row(
        modifier = Modifier
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        val S = LocalStrings.current

        if (filters.withNickname) {
            PlayerFilterChip(
                label = S.filterWithNickname,
                onRemove = onRemoveWithNickname
            )
        }

        if (filters.withoutNickname) {
            PlayerFilterChip(
                label = S.filterWithoutNickname,
                onRemove = onRemoveWithoutNickname
            )
        }

        if (filters.withPhoto) {
            PlayerFilterChip(
                label = S.filterWithPhoto,
                onRemove = onRemoveWithPhoto
            )
        }
    }
}

@Composable
fun PlayerFilterChip(
    label: String,
    onRemove: () -> Unit
) {
    val S = LocalStrings.current
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.secondaryContainer)
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium
        )

        Spacer(Modifier.width(6.dp))

        Icon(
            imageVector = Icons.Default.Close,
            contentDescription = S.remove,
            modifier = Modifier
                .size(16.dp)
                .clickable { onRemove() }
        )
    }
}

private fun PlayerSortOption.label(strings: Strings): String {
    return when (this) {
        PlayerSortOption.NAME_ASC -> strings.sortPlayersAZ
        PlayerSortOption.NAME_DESC -> strings.sortPlayersZA
    }
}
